import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { message } from 'antd';
import {
  GetShipmentAlertsPublically,
  GetSingleShipmentPublically,
} from '@/apis/shipment';
import { CheckPasswordAgaintUUID } from '@/apis/shipment';
import { useRefreshTelemetrySummary } from '@/pages/Shipments/SensorTimeline/queries';
import {
  AlertsResponse,
  CheckPasswordResponse,
  DisplayTimeSelection,
  ShipmentResponse,
} from '@/pages/Shipments/MapExternalView/types';

export function useSharePageData({
  id,
  uuid,
  enabled,
  pollMs = 30000,
  onUnauthorized,
  spinner,
  displayTime,
  onDefaultDisplayTime,
}: {
  id?: string;
  uuid?: string;
  enabled: boolean;
  pollMs?: number;
  onUnauthorized?: () => void;
  spinner?: { show?: () => void; hide?: () => void };
  displayTime: DisplayTimeSelection;
  onDefaultDisplayTime?: (sel: DisplayTimeSelection) => void;
}) {
  const unauthorizedRef = useRef(onUnauthorized);
  useEffect(() => {
    unauthorizedRef.current = onUnauthorized;
  }, [onUnauthorized]);

  const spinShowRef = useRef(spinner?.show);
  const spinHideRef = useRef(spinner?.hide);
  useEffect(() => {
    spinShowRef.current = spinner?.show;
    spinHideRef.current = spinner?.hide;
  }, [spinner]);

  const refreshTelemetrySummary = useRefreshTelemetrySummary();
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [shipment, setShipment] = useState({});
  const [alertsData, setAlertsData] = useState(null);
  const [companyId, setCompanyId] = useState<number | null>(null);

  const apiParams = useMemo(() => {
    return displayTime.type === 'timezone'
      ? { timezone: displayTime.value }
      : { display: displayTime.value };
  }, [displayTime]);

  const fetchPublicAlerts = useCallback(
    async (withSpin = false) => {
      if (!enabled || !id || !uuid) return;
      try {
        if (withSpin) {
          spinShowRef.current?.();
        }
        const res: AlertsResponse = await GetShipmentAlertsPublically(
          id,
          uuid,
          apiParams?.display || apiParams?.timezone
        );
        const d = res?.data;
        if (d?.data?.is_success) {
          setAlertsData(d?.data?.data ?? []);
        } else {
          message.error(d?.data?.message);
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 401)
          unauthorizedRef.current?.();
      } finally {
        if (withSpin) {
          spinHideRef.current?.();
        }
      }
    },
    [enabled, id, uuid, apiParams]
  );

  const fetchPublicShipment = useCallback(
    async (withSpin = false) => {
      if (!enabled || !id || !uuid) return;
      try {
        if (withSpin) {
          spinShowRef.current?.();
        }
        const res: ShipmentResponse = await GetSingleShipmentPublically(
          uuid,
          id,
          apiParams?.display || apiParams?.timezone
        );
        const d = res?.data;
        if (d?.data?.is_success) {
          const resp = d?.data?.data ?? null;
          setShipment(resp ?? {});
          setCompanyId(resp?.company ?? null);
        } else {
          message.error(d?.data?.message);
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 401)
          unauthorizedRef.current?.();
      } finally {
        if (withSpin) {
          spinHideRef.current?.();
        }
      }
    },
    [enabled, id, uuid, apiParams]
  );

  const loadAll = useCallback(
    async (withSpin = false) => {
      await Promise.all([
        fetchPublicAlerts(withSpin),
        fetchPublicShipment(withSpin),
      ]);
    },
    [fetchPublicAlerts, fetchPublicShipment]
  );

  useEffect(() => {
    const check = async () => {
      if (!enabled || !uuid) return;
      try {
        spinShowRef.current?.();
        const res: CheckPasswordResponse = await CheckPasswordAgaintUUID(uuid);
        const data = res?.data;
        if (data?.data?.is_success) {
          const tz = data?.data?.data?.preferred_timezone;
          if (tz && typeof tz === 'string') {
            try {
              new Date().toLocaleString('en-US', { timeZone: tz });
              onDefaultDisplayTime?.({ type: 'timezone', value: tz });
            } catch {
              onDefaultDisplayTime?.({ type: 'preset', value: tz });
            }
          }
          setPasswordRequired(!!data?.data?.data?.require_password);
        } else {
          message.error(data?.data?.message);
        }
      } catch (error) {
        message.error(
          axios.isAxiosError(error)
            ? error.response?.data?.message || 'Something went wrong'
            : 'Something went wrong'
        );
      } finally {
        spinHideRef.current?.();
      }
    };
    check();
  }, [enabled, uuid, onDefaultDisplayTime]);

  useEffect(() => {
    if (!enabled || passwordRequired) return;
    loadAll(true);
  }, [enabled, passwordRequired, id, uuid, loadAll]);

  useEffect(() => {
    if (!enabled || passwordRequired) return;
    const idInt = setInterval(() => {
      loadAll(false);
      if (id) {
        const _ = refreshTelemetrySummary(id);
      }
    }, pollMs);
    return () => clearInterval(idInt);
  }, [enabled, passwordRequired, pollMs, loadAll, id, refreshTelemetrySummary]);

  return {
    passwordRequired,
    setPasswordRequired,
    shipment,
    alertsData,
    companyId,
    refetch: () => loadAll(false),
  };
}
